import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import { EllipsisVertical, MessageSquare } from 'lucide-react';
import SectionHeading from '@/components/Post/SectionHeading';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserState } from '@/redux/auth/auth.d';

import {
  useGetProjectGroupByIdQuery,
  useGetProjectDetailsQuery,
  useGetProjectGroupPartsQuery,
  useDeleteProjectGroupMutation,
} from '@/redux/projectGroup/projectGroup.api.slice';
import Loader from '@/components/common/Loader';
import NotFound from '../NotFound';
import ShareButton from '@/components/Post/ShareButton';
import ProjectGroupLikeButton from '@/redux/like/ProjectGroupLikeButton';
import ProjectGroupBookmarkButton from '@/redux/bookmark/ProjectGroupBookmarkButton';
import ProjectGroupCommentList from '@/redux/commet/ProjectGroupCommentList';
import ProjectGroupCommentForm from '@/redux/commet/ProjectGroupCommentForm';
import DownloadProjectGroupButton from '@/components/ProjectGroup/DownloadProjectGroupButton';
import ProjectGroupBody from './ProjectGroupBody';
import UploadMakeModal from '@/redux/projectMake/UploadMakeModal';
import {
  ProjectMakes,
  useGetProjectMakesByGroupQuery,
} from '@/redux/projectMake';
import RatingButton from '@/redux/projectMake/RatingButton';
import Notice from '@/components/ui/notice';

// Count is URL/selection plumbing and render guards, not logic.
// fallow-ignore-next-line complexity
function ProjectGroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useSelector((state: { auth: UserState }) => state.auth);

  // Fetch group with light mode (thumbnails only)
  const { data: projectGroup, isLoading: groupLoading } =
    useGetProjectGroupByIdQuery({
      id: id || '',
      light: true,
    });

  const [deleteProjectGroup] = useDeleteProjectGroupMutation();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [showParts, setShowParts] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: makes, isLoading: isMakesLoading } =
    useGetProjectMakesByGroupQuery({ projectGroupId: id || '' }, { skip: !id });

  const isOwner = Boolean(user && projectGroup?.user?._id === user._id);

  // Mirrors the two server-side rules (403 own group / 409 duplicate) so the CTA
  // isn't offered for a submission the API would reject.
  const hasOwnMake = Boolean(
    user && makes?.some((make) => make.user?._id === user._id)
  );

  let makeAction = null;
  if (!isOwner && !hasOwnMake) {
    makeAction = <UploadMakeModal projectGroupId={id ?? ''} />;
  } else if (hasOwnMake) {
    makeAction = (
      <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">
        You&apos;ve already shared a make for this multi workpiece — one per
        maker.
      </p>
    );
  } else if (isOwner) {
    makeAction = (
      <p className="rounded-md border border-border p-4 text-sm text-muted-foreground">
        Makes come from other makers who cut your project — you can&apos;t post
        one on your own.
      </p>
    );
  }

  const handleEdit = () => {
    navigate(`/project/v2/group/${id}/edit`);
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteProjectGroup(id).unwrap();
      toast.success('Multi Workpiece deleted');
      navigate(-1);
    } catch (error) {
      toast.error('Failed to delete the Multi Workpiece. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  // Initialize selected project from URL or auto-select first project
  useEffect(() => {
    if (!projectGroup?.projects?.length) return;

    const urlProjectId = searchParams.get('selected');

    // If URL has a valid project ID, use it
    if (
      urlProjectId &&
      projectGroup.projects.some((p) => p._id === urlProjectId)
    ) {
      setSelectedProjectId(urlProjectId);
    }
    // Otherwise auto-select first project
    else if (!selectedProjectId) {
      const firstProjectId = projectGroup.projects[0]._id;
      setSelectedProjectId(firstProjectId);
      // Update URL to reflect auto-selection
      setSearchParams({ selected: firstProjectId }, { replace: true });
    }
  }, [projectGroup, searchParams, selectedProjectId, setSearchParams]);

  // Fetch selected project details on-demand
  const { data: selectedProject, isLoading: projectLoading } =
    useGetProjectDetailsQuery(
      {
        groupId: id!,
        projectId: selectedProjectId!,
      },
      {
        skip: !id || !selectedProjectId, // Only fetch when both IDs are available
      }
    );

  // Fetch parts data on-demand (deferred loading)
  const { data: partsData, isLoading: partsLoading } =
    useGetProjectGroupPartsQuery(id!, {
      skip: !showParts || !id, // Only fetch when user scrolls to parts section
    });

  // Intersection observer for parts section
  const partsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShowParts(true); // Trigger parts data fetch
        }
      },
      { threshold: 0.1 }
    );

    if (partsRef.current) {
      observer.observe(partsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Use the full project data from on-demand query
  const currentProject = selectedProject;

  if (groupLoading) return <Loader title="Just a moment!" description="" />;

  if (!projectGroup || !id) {
    return <NotFound />;
  }

  return (
    <div
      className="overflow-y-auto scroll-snap-type-y-mandatory pb-24"
      style={{ marginTop: 'var(--nav-height)' }}
    >
      {projectGroup.visibility === 'private' && (
        <Notice className="mx-3 mt-4 sm:mx-5">
          This multi workpiece is private and not visible to others.
        </Notice>
      )}
      <ProjectGroupBody
        group={projectGroup}
        currentProject={currentProject}
        isProjectLoading={projectLoading}
        selectedProjectId={selectedProjectId}
        onSelectProject={(projectId) => {
          setSelectedProjectId(projectId);
          setSearchParams({ selected: projectId }, { replace: true });
        }}
        parts={showParts ? partsData : undefined}
        isPartsLoading={showParts && partsLoading}
        partsRef={partsRef}
        titleActions={
          isOwner && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <EllipsisVertical />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={handleEdit}>
                  Edit Group
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="focus:text-red-500 focus:bg-red-200"
                >
                  Delete Group
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        }
        interactions={
          <>
            <ProjectGroupLikeButton
              liked={projectGroup.hasLikedByUser}
              projectGroupId={id}
              likes={projectGroup.likes}
              size="md"
            />
            <RatingButton
              averageTotalRating={projectGroup.averageTotalRating ?? undefined}
              makes={makes}
              isLoading={isMakesLoading}
              size="md"
            />
            <ProjectGroupBookmarkButton
              bookmarked={projectGroup.hasBookmarkedByUser}
              projectGroupId={id}
              size="icon"
            />
            <ShareButton
              shareUrl={`${window.location.origin}/project-groups/${id}`}
              size="icon"
            />
          </>
        }
        download={
          <DownloadProjectGroupButton
            projectGroupId={id}
            projectGroupName={projectGroup.name}
            totalPartsCount={partsData?.totalPartsCount}
            projects={projectGroup.projects}
          />
        }
      />
      {/* Makes and comments share the row 50/50, same as PostDetail. */}
      <div className="border-t border-border scroll-snap-align-start mt-8 pt-8 pb-8 mx-auto px-4 lg:px-14 flex items-start gap-10 lg:flex-nowrap flex-wrap">
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <ProjectMakes projectGroupId={id} action={makeAction} />
        </div>
        <div className="w-full lg:w-1/2 flex flex-col gap-4">
          <SectionHeading
            icon={<MessageSquare className="w-5 h-5 text-primary" />}
            count={projectGroup.comments}
          >
            Comments
          </SectionHeading>
          <Card className="p-4 md:p-5 flex flex-col">
            <ProjectGroupCommentForm projectGroupId={id} />
            <div className="mt-5 pt-5 border-t border-border">
              <ProjectGroupCommentList projectGroupId={id} />
            </div>
          </Card>
        </div>
      </div>

      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this group? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-4">
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectGroupDetail;
